<template>
  <div>
    <table class="tw-w-full">
      <tbody>
        <tr>
          <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
            Subtotal
          </td>
          <td
            class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4"
            :aria-busy="showTotalsShimmer"
          >
            <span :class="amountValueClass">
              ${{ formatCurrency(basketStore.basket?.subtotal ?? null) }}
            </span>
          </td>
        </tr>
        
        <template v-if="basketStore.basket?.discounts != null && basketStore.basket?.discounts.length > 0">
          <tr>
            <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
              Discounts
            </td>
            <td class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4">
              
            </td>
          </tr>

          <BagDiscount
            v-for="(discount, index) in basketStore.basket?.discounts"
            :key="'discount-'+(discount as Discount).couponcode"
            @emit-removeDiscount="clickedRemoveDiscount"
            :discount="discount">
          </BagDiscount>
        </template>

        <tr v-if="basketStore.basket && basketStore.basket.tip">
          <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
            Tip
          </td>
          <td
            class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4"
            :aria-busy="showTotalsShimmer"
          >
            <span :class="amountValueClass">
              ${{ formatCurrency(basketStore.basket?.tip ?? null) }}
            </span>
          </td>
        </tr>

        <tr>
          <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
            Estimated Tax
          </td>
          <td
            class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4"
            :aria-busy="showTotalsShimmer"
          >
            <span :class="amountValueClass">
              ${{ formatCurrency(basketStore.basket?.salestax ?? null) }}
            </span>
          </td>
        </tr>

        <tr v-if="basketStore.basket && basketStore.basket.customerhandoffcharge">
          <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
            Delivery Fee
          </td>
          <td
            class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4"
            :aria-busy="showTotalsShimmer"
          >
            <span :class="amountValueClass">
              ${{ formatCurrency(basketStore.basket?.customerhandoffcharge ?? null) }}
            </span>
          </td>
        </tr>

        <tr 
          v-for="(fee, index) in basketStore.basket && basketStore.basket.fees"
          :key="'fee-'+index"
        >
          <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
            {{fee.description}}
          </td>
          <td
            class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4"
            :aria-busy="showTotalsShimmer"
          >
            <span :class="amountValueClass">
              ${{ formatCurrency(fee.amount) }}
            </span>
          </td>
        </tr>

        <tr>
          <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
            Total
          </td>
          <td
            class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4"
            :aria-busy="showTotalsShimmer"
          >
            <span :class="amountValueClass">
              ${{ formatCurrency(basketStore.basket?.total ?? null) }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// Data
import { useBasketStore } from "../../stores/basket";
import type Discount from '../../types/Discount';

import BagDiscount from './_BagDiscount.vue';

const basketStore = useBasketStore();

const emit = defineEmits(['emitRemoveDiscount', 'emitRemoveDiscount'])

const showTotalsShimmer = computed(() => basketStore.hasPendingMutations);

const amountValueBaseClass = 'amount-value tw-inline-flex tw-justify-end tw-min-w-[5.5rem] tw-leading-none tw-px-1';
const amountValueShimmerClass = `${amountValueBaseClass} amount-value--loading`;

const amountValueClass = computed(() => (showTotalsShimmer.value ? amountValueShimmerClass : amountValueBaseClass));

const formatCurrency = (value?: number | null): string => {
  const numericValue = typeof value === 'string' ? Number(value) : value;

  if (typeof numericValue === 'number' && Number.isFinite(numericValue)) {
    return numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  return '0.00';
};

const clickedRemoveDiscount = (discount: Discount) => {
  emit('emitRemoveDiscount', discount)
}

</script>

<style scoped>
.amount-value {
    position: relative;
    transition: color 0.2s ease;
}

.amount-value--loading {
    color: transparent;
    background-image: linear-gradient(
        110deg,
        #4b5563 0%,
        #9ca3af 35%,
        #f9fafb 50%,
        #9ca3af 65%,
        #4b5563 100%
    );
    background-size: 420% 100%;
    background-position: 380% 50%;
    animation: amount-value-shimmer 3s linear infinite;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

@keyframes amount-value-shimmer {
    0% {
        background-position: 380% 50%;
    }
    100% {
        background-position: -380% 50%;
    }
}
</style>